import { IText } from 'fabric';
import type { BasicTransformEvent, Canvas, FabricObject, TPointerEvent } from 'fabric';
import type { TypedEventEmitter } from '@/utils/TypedEventEmitter';
import type { TViewTransform } from '@/application/ports/TViewTransform';

type TFabricModifiedEvent = BasicTransformEvent<TPointerEvent> & { target: FabricObject };

type TFabricEngineEvents = {
    selection: string | undefined;
    textChanged: { id: string; text: string };
    textSelection: { end: number; start: number } | undefined;
    transform: TViewTransform;
    zoom: number;
};

type TFabricEventHandlerParams = {
    canvas: Canvas;
    events: TypedEventEmitter<TFabricEngineEvents>;
};

// Translates fabric-native canvas events into typed emits on the
// engine's bus.
class FabricEventHandler {
    private readonly _boundOnModified: (event: TFabricModifiedEvent) => void;
    private readonly _boundOnSelectionCleared: () => void;
    private readonly _boundOnSelectionCreated: (event: { selected?: FabricObject[] }) => void;
    private readonly _boundOnSelectionUpdated: (event: { selected?: FabricObject[] }) => void;
    private readonly _boundOnTextChanged: (event: { target: FabricObject }) => void;
    private _boundOnTextSelectionChanged?: () => void;
    private readonly _canvas: Canvas;
    private readonly _events: TypedEventEmitter<TFabricEngineEvents>;
    private _textSelectionAttachedText?: IText;

    constructor(params: TFabricEventHandlerParams) {
        this._canvas = params.canvas;
        this._events = params.events;
        this._boundOnModified = (event) => this._onObjectModified(event);
        this._boundOnTextChanged = (event) => this._onTextChanged(event);
        this._boundOnSelectionCreated = (event) => this._onSelectionChanged(event.selected?.[0]);
        this._boundOnSelectionUpdated = (event) => this._onSelectionChanged(event.selected?.[0]);
        this._boundOnSelectionCleared = () => this._onSelectionChanged(undefined);
        this._canvas.on('object:modified', this._boundOnModified);
        this._canvas.on('text:changed', this._boundOnTextChanged);
        this._canvas.on('selection:created', this._boundOnSelectionCreated);
        this._canvas.on('selection:updated', this._boundOnSelectionUpdated);
        this._canvas.on('selection:cleared', this._boundOnSelectionCleared);
    }

    dispose(): void {
        this._canvas.off('object:modified', this._boundOnModified);
        this._canvas.off('text:changed', this._boundOnTextChanged);
        this._canvas.off('selection:created', this._boundOnSelectionCreated);
        this._canvas.off('selection:updated', this._boundOnSelectionUpdated);
        this._canvas.off('selection:cleared', this._boundOnSelectionCleared);
        this._detachTextSelectionListener();
    }

    private _onObjectModified(event: TFabricModifiedEvent): void {
        const transform = _toViewTransform(event.target);
        if (transform) {
            this._events.emit('transform', transform);
        }
    }

    private _onTextChanged(event: { target: FabricObject }): void {
        const target = event.target as FabricObject & { id?: string; text?: string };
        if (target.type !== 'textbox' || !target.id || target.text === undefined) {
            return;
        }
        // Fabric fires no `selection:changed` when characters are inserted, so a range taken
        // before the edit would live on and point into the new text. An insertion leaves a
        // caret and no selection, so drop it: without this, typing over a selection then
        // changing a style applied that style to a slice of the replacement instead of to the
        // whole textbox, and a shorter replacement pushed the range past the end of the text.
        this._events.emit('textSelection', undefined);
        this._events.emit('textChanged', { id: target.id, text: target.text });
    }

    private _onSelectionChanged(target: FabricObject | undefined): void {
        const entityId = (target as (FabricObject & { id?: string }) | undefined)?.id;
        this._events.emit('selection', entityId);
        this._rewireTextSelectionListener(target);
        this._events.emit('textSelection', undefined);
    }

    // Character-range selection inside a textbox needs a per-text
    // `selection:changed` listener — canvas-level events don't carry it.
    private _rewireTextSelectionListener(target: FabricObject | undefined): void {
        this._detachTextSelectionListener();
        if (!(target instanceof IText)) {
            return;
        }
        const text = target;
        this._boundOnTextSelectionChanged = () => {
            const start = text.selectionStart ?? 0;
            const end = text.selectionEnd ?? 0;
            this._events.emit('textSelection', start === end ? undefined : { end, start });
        };
        // Fabric fires no `selection:changed` on insertion, so this listener alone would let a
        // range outlive the characters it pointed at — _onTextChanged clears it instead.
        text.on('selection:changed', this._boundOnTextSelectionChanged);
        this._textSelectionAttachedText = text;
    }

    private _detachTextSelectionListener(): void {
        if (this._textSelectionAttachedText && this._boundOnTextSelectionChanged) {
            this._textSelectionAttachedText.off('selection:changed', this._boundOnTextSelectionChanged);
            this._textSelectionAttachedText = undefined;
            this._boundOnTextSelectionChanged = undefined;
        }
    }
}

// Fabric v6 emits 'Image'/'Textbox' (capitalized) from real pointer
// events, so the lowercase-only switch drops them — matches the
// safety-net spec.
function _toViewTransform(target: FabricObject): TViewTransform | null {
    const targetType = target.type;
    if (targetType !== 'image' && targetType !== 'textbox') {
        return null;
    }
    const { id } = target as FabricObject & { id?: string };
    if (!id) {
        return null;
    }

    // Round position + rotation to integer pixels/degrees to match the
    // format persisted by main. Scale + size stay float — main didn't
    // round those either, and fabric supports sub-unit values there.
    const transform: TViewTransform = {
        id,
        position: { x: Math.round(target.left), y: Math.round(target.top) },
        rotationDeg: Math.round(target.angle),
        scale: { x: target.scaleX, y: target.scaleY },
    };
    if (targetType === 'textbox') {
        transform.size = { height: target.height, width: target.width };
    }

    return transform;
}

export { FabricEventHandler };
export type { TFabricEngineEvents };
